import { useState, useMemo, useEffect } from 'react';
import EnterpriseModal from './EnterpriseModal';
import api from '../api/axios';
// ---------------------------------------------------------------------------
// Employer pricing: plan cards + "View all features" comparison.
// Every price, limit, badge, label and bullet comes from the plan records the
// admin saves (GET /plans?type=subscription). Nothing here is hard-coded.
// ---------------------------------------------------------------------------

// TEMPORARY GUARD: the backend (SubscriptionController) currently accepts monthly
// purchases only. Set this to true once yearly checkout is supported server-side.
const YEARLY_CHECKOUT_READY = true;

// Edit these two lines to your real tax and refund policy.
export const TAX_NOTE = 'Prices shown are the final amount payable.';
export const REFUND_NOTE = 'Your plan or add-on is activated right after payment. Contact support for any billing issue.';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? '' : 's'}`;

// Used only when the admin has not written a bullet list for a plan.
// Built from the plan's own fields, so it still follows admin data.
const fallbackBullets = (p, yearly = false) => {
  const unlocks = yearly ? p.yearly_contact_unlocks : p.contact_unlocks_per_job;
  const featured = yearly ? p.yearly_featured_jobs : p.featured_jobs;
  const boosts = yearly ? p.yearly_boost_credits : p.boost_credits;
  const per = yearly ? 'year' : 'month';
  return [
    p.job_post_limit == null ? 'Unlimited active jobs' : plural(p.job_post_limit, 'active job'),
    p.job_validity_days != null && `${p.job_validity_days} days job validity`,
    unlocks != null && `${unlocks} candidate unlocks / ${per}`,
    Number(featured) > 0 && `${plural(featured, 'Featured Job')} / ${per}`,
    Number(boosts) > 0 && `${plural(boosts, 'Job Boost')} / ${per}`,
  ].filter(Boolean);
};

const countOrNone = (v) => (v == null || Number(v) === 0 ? 'None' : v);

// Yearly line shown under the admin bullets in the yearly view.
const yearlySummary = (p) => [
  p.yearly_contact_unlocks == null ? 'Unlimited candidate unlocks' : Number(p.yearly_contact_unlocks) > 0 && `${p.yearly_contact_unlocks} candidate unlocks / year`,
  Number(p.yearly_featured_jobs) > 0 && `${plural(p.yearly_featured_jobs, 'Featured Job')} / year`,
  Number(p.yearly_boost_credits) > 0 && `${plural(p.yearly_boost_credits, 'Job Boost')} / year`,
].filter(Boolean);

// Row label can be a string or a function(yearly). get(plan, yearly) returns the value.
const LIMIT_ROWS = [
  ['Active jobs', (p) => (p.job_post_limit == null ? 'Unlimited' : p.job_post_limit)],
  ['Job validity', (p) => (p.job_validity_days != null ? `${p.job_validity_days} days` : '—')],
  ['Applications per job', (p) => (p.application_limit == null ? 'Unlimited' : p.application_limit)],
  [(y) => `Candidate unlocks / ${y ? 'year' : 'month'}`, (p, y) => {
    const v = y ? p.yearly_contact_unlocks : p.contact_unlocks_per_job;
    return v == null ? 'Unlimited' : countOrNone(v);
  }],
  [(y) => `Featured jobs / ${y ? 'year' : 'month'}`, (p, y) => countOrNone(y ? p.yearly_featured_jobs : p.featured_jobs)],
  [(y) => `Job boosts / ${y ? 'year' : 'month'}`, (p, y) => countOrNone(y ? p.yearly_boost_credits : p.boost_credits)],
  ['Recruiter seats', (p) => p.recruiter_seats ?? 1],
];
const rowLabel = (l, y) => (typeof l === 'function' ? l(y) : l);

const FLAG_ROWS = [
  ['ai_features_enabled', 'AI features'],
  ['candidate_search_enabled', 'Candidate search'],
  ['advanced_filters_enabled', 'Advanced filters'],
  ['ai_matching_enabled', 'AI matching'],
  ['ai_shortlist_enabled', 'AI shortlist'],
  ['interview_tools_enabled', 'Interview tools'],
  ['analytics_enabled', 'Analytics'],
  ['whatsapp_contact_enabled', 'WhatsApp contact'],
  ['bulk_actions_enabled', 'Bulk actions'],
  ['priority_support', 'Priority support'],
];

const isEnterprise = (p) => /enterprise|corporate/i.test(p.name || '');
const isFreePlan = (p) => p.billing_type === 'free' || Number(p.price_monthly) === 0;

const track = (event, data = {}) => {
  api.post('/billing-analytics/track', { event, ...data }).catch(() => {});
};

export default function EmployerPlansTab({
  plans,
  current,
  billingCycle,
  onBillingCycleChange,
  activating,
  autoPayLoading,
  onBuy,
  onAutoPay,
  onFreePlan,
  onScheduleDowngrade,
  pendingPlanName,
   autoPayOn = false,
  onAutoPayToggle,
}) {
  const [showAll, setShowAll] = useState(false);
  const [showEnterprise, setShowEnterprise] = useState(false);


    const [corp, setCorp] = useState(null);
  useEffect(() => {
    api.get('/enterprise-card').then(({ data }) => setCorp(data || null)).catch(() => {});
  }, []);

  const corpBadge = corp?.badge || 'Corporate';
  const corpTitle = corp?.title || 'Custom pricing';
  const corpDesc = corp ? (corp.description || '') : 'For companies with large-scale recruitment needs.';
  const corpButton = corp?.button_label || 'Talk to Sales';
  const corpFeatures = Array.isArray(corp?.features) && corp.features.length
    ? corp.features
    : ['Bulk job posting', 'Multiple HR users', 'Multi-location recruitment', 'Centralised recruitment dashboard', 'Custom analytics', 'API integrations, subject to availability', 'Dedicated account management'];

    useEffect(() => {
    track('pricing_page_viewed', { source: 'billing' });
  }, []);

  const hasYearly = plans.some((p) => Number(p.price_yearly) > 0);

  // Shown in the toggle label only. Prices are never calculated here.
  const saving = useMemo(() => {
    const pcts = plans
      .filter((p) => Number(p.price_monthly) > 0 && Number(p.price_yearly) > 0)
      .map((p) => Math.round((1 - Number(p.price_yearly) / (Number(p.price_monthly) * 12)) * 100))
      .filter((n) => n > 0);
    return pcts.length ? Math.max(...pcts) : 0;
  }, [plans]);

    const currentName = (current?.plan_name || 'free').toLowerCase();
    
  // Older subscriptions without a cycle count as monthly
  const currentCycle = current?.billing_cycle === 'yearly' ? 'yearly' : 'monthly';

  // Same plan AND same billing cycle (free plan has no cycle)
  const isCurrentPlan = (plan) =>
    (plan.name || '').toLowerCase() === currentName &&
    (isFreePlan(plan) || currentCycle === billingCycle);

  // Same plan, but the other billing cycle is selected in the toggle
  const isSamePlanOtherCycle = (plan) =>
    (plan.name || '').toLowerCase() === currentName &&
    !isFreePlan(plan) &&
    !isCurrentPlan(plan);
  const currentPlan = plans.find((p) => (p.name || '').toLowerCase() === currentName);
  const currentPaid = !!currentPlan && !isFreePlan(currentPlan);
  const currentPrice = Number(currentPlan?.price_monthly || 0);

  const daysLeft = current?.expires_at
    ? Math.ceil((new Date(current.expires_at) - new Date()) / 86400000)
    : null;
  const renewSoon = currentPaid && !current?.auto_renew && daysLeft != null && daysLeft <= 7;

  const yearlyBlocked = billingCycle === 'yearly' && !YEARLY_CHECKOUT_READY;

 const [selectedKey, setSelectedKey] = useState(null);
const selectedPlan = plans.find((p) => (p.id || p.name) === selectedKey) || null;

useEffect(() => {
  if (!selectedPlan) return;
  const onKey = (e) => e.key === 'Escape' && setSelectedKey(null);
  document.addEventListener('keydown', onKey);
  const prev = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  return () => {
    document.removeEventListener('keydown', onKey);
    document.body.style.overflow = prev;
  };
}, [selectedPlan]);

useEffect(() => {
  if (selectedPlan) track('plan_card_viewed', { plan_name: selectedPlan.name });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedKey]);

  const buildCta = (plan) => {
    const isCurrent = isCurrentPlan(plan);
    const free = isFreePlan(plan);

    if (isEnterprise(plan)) {
      return { label: plan.cta_label || 'Contact sales', action: () => setShowEnterprise(true) };
    }
    if (isCurrent) {
      if (free) return { label: plan.cta_label || 'Post Free Job', action: onFreePlan };
      if (renewSoon) return { label: 'Renew', buy: true };
      return { label: 'Current plan', disabled: true };
    }
    if (pendingPlanName && (plan.name || '').toLowerCase() === pendingPlanName.toLowerCase()) {
      return { label: 'Downgrade scheduled', disabled: true };
    }

        if (isSamePlanOtherCycle(plan)) {
      if (billingCycle === 'yearly') {
        return { label: 'Switch to yearly', buy: true };
      }
      return {
        label: 'Switch to monthly at renewal',
        downgrade: true,
        note: 'Your yearly plan stays active until it ends, then switches to monthly.',
      };
    }
    if (currentPaid && Number(plan.price_monthly) < currentPrice) {
      return { label: 'Schedule downgrade', downgrade: true, note: 'Your current plan stays active until it ends, then switches to this plan.' };
    }
    if (free) return { label: plan.cta_label || 'Post Free Job', action: onFreePlan };

    return { label: currentPaid ? 'Upgrade' : (plan.cta_label || 'Choose plan'), buy: true };
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 mb-6 border border-[#F3E8FF]">
     <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
  <div>
    <h3 className="font-extrabold text-[#3B0764] text-xl">Hire faster with PlanB Jobs</h3>
    <p className="text-sm text-gray-500 mt-1">Post jobs. Find candidates. Contact them. Hire faster.</p>
  </div>

  {hasYearly && (
    <div className="flex bg-[#FDF4FF] rounded-lg p-1 border border-[#F3E8FF]">
      {[
        { key: 'monthly', label: 'Monthly' },
        { key: 'yearly', label: saving > 0 ? `Yearly · Save ${saving}%` : 'Yearly' },
      ].map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onBillingCycleChange(opt.key)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
            billingCycle === opt.key
              ? 'bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white shadow-sm'
              : 'text-gray-500'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )}
</div>



      {plans.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-10">No plans available right now.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((plan) => {
              const isCurrent = isCurrentPlan(plan);
              const free = isFreePlan(plan);
              const showYearly = billingCycle === 'yearly' && Number(plan.price_yearly) > 0;
              const price = showYearly ? plan.price_yearly : plan.price_monthly;
              const period = free ? 'forever' : showYearly ? 'per year' : 'per month';

              const adminBullets = Array.isArray(plan.features) ? plan.features.filter(Boolean) : [];
              const bullets = (adminBullets.length ? adminBullets : fallbackBullets(plan, showYearly)).slice(0, 6);
const yearlyLine = showYearly && adminBullets.length ? yearlySummary(plan) : [];

              let cta = buildCta(plan);
              if (cta.buy && yearlyBlocked) {
                cta = { label: 'Yearly checkout coming soon', disabled: true };
              }

              if (cta.buy && billingCycle === 'yearly' && !showYearly) {
  cta = { label: 'Monthly plan only', disabled: true };
}

              const busy = activating === plan.name;
              const canAutoPay =
                !free &&
                !isEnterprise(plan) &&
                billingCycle === 'monthly' &&
                !current?.auto_renew &&
                cta.label !== 'Downgrade';

              return (
                <div
  key={plan.id || plan.name}
  onClick={() => setSelectedKey(plan.id || plan.name)}
  className={`relative cursor-pointer rounded-2xl p-6 flex flex-col border-2 transition ${
                    plan.is_best_value
                      ? 'order-first sm:order-none border-[#EC4899] bg-gradient-to-br from-[#FDF4FF] to-white shadow-xl'
                      : isCurrent
                      ? 'border-[#9333EA] bg-[#FDF4FF]'
                      : 'border-[#F3E8FF] bg-white hover:border-[#EC4899] hover:shadow-lg'
                  }`}
                >
                  {plan.is_best_value && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                      Best Value
                    </span>
                  )}

                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-base font-extrabold uppercase tracking-wide text-[#7E22CE]">{plan.name}</p>
                    {isCurrent && (
                      <span className="text-[10px] font-bold bg-[#9333EA] text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                        Current plan
                      </span>
                    )}
                  </div>

                  <p className="text-4xl font-extrabold mb-0.5 text-[#7E22CE]">
                    {fmt(price)}
                  </p>
                  <p className="text-xs text-gray-400 mb-3">{period}</p>

                  {plan.short_description && (
                    <p className="text-xs text-gray-500 mb-4">{plan.short_description}</p>
                  )}

                  <ul className="space-y-2 mb-6 flex-1">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-gray-600">
                       <span className="w-2.5 h-2.5 mt-1.5 rounded-full bg-[#DB2777] shrink-0"></span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                                    {yearlyLine.length > 0 && (
                    <p className="text-[11px] font-semibold text-[#7E22CE] bg-[#FDF4FF] rounded-lg px-3 py-2 mb-4">
                      Yearly includes: {yearlyLine.join(' · ')}
                    </p>
                  )}
                      <p className="text-xs text-center text-[#9333EA] font-semibold mb-2"></p>
                  <button
                    type="button"
                    disabled={cta.disabled || busy || autoPayLoading === plan.name}
                    title={cta.note || undefined}
                    onClick={(e) => {
  e.stopPropagation();
  track('plan_cta_clicked', { plan_name: plan.name, label: cta.label, source: 'card' });
  if (cta.buy || cta.downgrade) {
    setSelectedKey(plan.id || plan.name);
  } else if (cta.action) {
    cta.action();
  }
}}
                    className={`py-3 rounded-xl text-sm font-bold disabled:opacity-50 transition ${
                      plan.is_best_value
                        ? 'bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white hover:shadow-lg'
                        : 'border-2 border-[#9333EA] text-[#9333EA] hover:bg-[#FDF4FF]'
                    }`}
                  >
                                   {busy || autoPayLoading === plan.name ? 'Opening…' : cta.label}

               </button>
                </div>
              );
            })}
          </div>

                   {!plans.some(isEnterprise) && !(corp && corp.is_active === false) && (
            <div className="mt-5 relative overflow-hidden rounded-2xl bg-[#17142A] p-6 sm:p-8 text-white">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#7047FF]/30 blur-3xl pointer-events-none" aria-hidden="true" />
              <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 items-center">
                <div>
                  <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-[#C7F464] text-[#17142A] px-3 py-1 rounded-full">
                  {corpBadge}
                  </span>
                  <p className="text-3xl sm:text-4xl font-bold mt-4" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                    {corpTitle}
                  </p>
                  <p className="text-sm text-white/70 mt-2 max-w-sm">
                    {corpDesc}
                  </p>
                  <button
                    type="button"
                    onClick={() => { track('plan_cta_clicked', { plan_name: 'Corporate', label: 'Talk to Sales', source: 'card' }); setShowEnterprise(true); }}
                    className="mt-6 py-3 px-8 rounded-xl text-sm font-bold bg-[#C7F464] text-[#17142A] hover:bg-[#B6E84A] hover:shadow-lg transition"
                  >
                  {corpButton}
                  </button>
                </div>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 items-start">
                  {corpFeatures.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-white/90">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#C7F464] text-[#17142A] flex items-center justify-center text-[11px] font-bold shrink-0">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

           <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E9D5FF] bg-[#F5E9FF] px-5 py-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="text-xs font-extrabold uppercase tracking-wide text-[#6B21A8]">Add-ons</span>
              <span className="text-sm font-bold text-[#3B0764]">
                Candidate Credits <span className="mx-2 text-[#7E22CE]">•</span>
                Job Boost <span className="mx-2 text-[#7E22CE]">•</span>
                Featured Job
              </span>
            </div>
            <a
              href="?tab=credits"
              className="text-sm font-bold text-[#7E22CE] hover:underline"
            >
              View all add-ons →
            </a>
          </div>

          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => setShowAll((s) => !s)}
              className="text-sm font-semibold text-[#9333EA] hover:underline"
            >
              {showAll ? 'Hide all features' : 'View all features'}
            </button>
          </div>

          {showAll && (
            <div className="mt-4 overflow-x-auto border border-[#F3E8FF] rounded-2xl">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FDF4FF] text-[#3B0764]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Feature</th>
                    {plans.map((p) => (
                      <th key={p.id || p.name} className="px-4 py-3 text-center font-semibold">{p.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                 {LIMIT_ROWS.map(([label, get]) => (
  <tr key={rowLabel(label, false)} className="border-t border-[#F3E8FF]">
    <td className="px-4 py-2.5 text-gray-600">{rowLabel(label, billingCycle === 'yearly')}</td>
    {plans.map((p) => (
      <td key={p.id || p.name} className="px-4 py-2.5 text-center font-semibold text-[#3B0764]">
        {get(p, billingCycle === 'yearly' && Number(p.price_yearly) > 0)}
      </td>
    ))}
  </tr>
))}
                  {FLAG_ROWS.map(([key, label]) => (
                    <tr key={key} className="border-t border-[#F3E8FF]">
                      <td className="px-4 py-2.5 text-gray-600">{label}</td>
                      {plans.map((p) => (
                        <td key={p.id || p.name} className="px-4 py-2.5 text-center">
                          {p[key] ? <span className="text-[#9333EA] font-bold">✓</span> : <span className="text-gray-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
{selectedPlan && (() => {
  const plan = selectedPlan;
  const free = isFreePlan(plan);
  let cta = buildCta(plan);
 const showYearly = billingCycle === 'yearly' && Number(plan.price_yearly) > 0;
if (cta.buy && yearlyBlocked) cta = { label: 'Yearly checkout coming soon', disabled: true };
if (cta.buy && billingCycle === 'yearly' && !showYearly) cta = { label: 'Monthly plan only', disabled: true };
  const price = showYearly ? plan.price_yearly : plan.price_monthly;
  const period = free ? 'forever' : showYearly ? '/ year' : '/ month';
  const adminBullets = Array.isArray(plan.features) ? plan.features.filter(Boolean) : [];
  const bullets = adminBullets.length ? adminBullets : fallbackBullets(plan, showYearly);
  const busy = activating === plan.name || autoPayLoading === plan.name;
  const canAutoPay =
    !free && !isEnterprise(plan) && billingCycle === 'monthly' &&
    !current?.auto_renew && cta.label !== 'Downgrade';

 const label = busy ? 'Opening…' : cta.label;
  return (
    <div
  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
  onClick={() => setSelectedKey(null)}
>
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[#6D28D9] via-[#9333EA] to-[#EC4899] text-white p-6 rounded-t-2xl">
          <button
            type="button"
            onClick={() => setSelectedKey(null)}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 font-bold"
            aria-label="Close"
          >
            ✕
          </button>
          <p className="text-lg font-extrabold">{plan.name}</p>
          <p className="text-4xl font-extrabold">
            {fmt(price)} <span className="text-sm font-normal text-white/80">{period}</span>
          </p>
          {plan.short_description && <p className="text-sm text-white/90 mt-2">{plan.short_description}</p>}
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h4 className="font-bold text-[#3B0764] mb-3">What's included</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="w-4 h-4 mt-0.5 rounded-full bg-[#FDF4FF] text-[#9333EA] flex items-center justify-center text-[10px] font-bold shrink-0">✓</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-[#3B0764] mb-3">Plan limits</h4>
            <div className="border border-[#F3E8FF] rounded-xl divide-y divide-[#F3E8FF]">
             {LIMIT_ROWS.map(([lbl, get]) => (
  <div key={rowLabel(lbl, false)} className="flex justify-between px-4 py-2.5 text-sm">
    <span className="text-gray-600">{rowLabel(lbl, showYearly)}</span>
    <span className="font-semibold text-[#3B0764]">{get(plan, showYearly)}</span>
  </div>
))}
            </div>
          </div>

          <div>
            <h4 className="font-bold text-[#3B0764] mb-3">Features</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FLAG_ROWS.map(([key, rowLabel]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  {plan[key]
                    ? <span className="text-[#9333EA] font-bold">✓</span>
                    : <span className="text-gray-300">—</span>}
                  <span className={plan[key] ? 'text-gray-700' : 'text-gray-400'}>{rowLabel}</span>
                </div>
              ))}
            </div>
          </div>

          {canAutoPay && cta.buy && (
            <div className="flex items-center gap-3 bg-[#FDF4FF] border border-[#F3E8FF] rounded-xl p-4">
              <button
                type="button"
                role="switch"
                aria-checked={autoPayOn}
                onClick={onAutoPayToggle}
                className={`relative w-12 h-6 rounded-full transition shrink-0 ${
                  autoPayOn ? 'bg-gradient-to-r from-[#9333EA] to-[#EC4899]' : 'bg-gray-300'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${autoPayOn ? 'translate-x-6' : ''}`} />
              </button>
              <p className="text-sm text-[#3B0764] font-semibold">
                {autoPayOn ? 'Autopay on: renews monthly, cancel anytime' : 'One-time payment'}
              </p>
            </div>
          )}

          {(cta.buy || cta.downgrade) && (
            <div>
              <h4 className="font-bold text-[#3B0764] mb-3">Order summary</h4>
              <div className="border border-[#F3E8FF] rounded-xl divide-y divide-[#F3E8FF] text-sm">
                <div className="flex justify-between px-4 py-2.5"><span className="text-gray-600">Plan</span><span className="font-semibold text-[#3B0764]">{plan.name}</span></div>
                <div className="flex justify-between px-4 py-2.5"><span className="text-gray-600">Billing</span><span className="font-semibold text-[#3B0764]">{showYearly ? 'Yearly' : 'Monthly'}</span></div>
                <div className="flex justify-between px-4 py-2.5"><span className="text-gray-600">Plan validity</span><span className="font-semibold text-[#3B0764]">{showYearly ? '1 year' : `${plan.pack_validity_days ?? 30} days`}</span></div>
                <div className="flex justify-between px-4 py-2.5"><span className="text-gray-600">{cta.downgrade ? 'Due today' : 'Total'}</span><span className="font-extrabold text-[#3B0764]">{cta.downgrade ? '₹0' : fmt(price)}</span></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{TAX_NOTE}</p>
              <p className="text-xs text-gray-500">{REFUND_NOTE}</p>
            </div>
          )}

          {cta.note && <p className="text-xs text-gray-500">{cta.note}</p>}

          <button
            type="button"
            disabled={cta.disabled || busy}
            onClick={() => {
  track('plan_cta_clicked', { plan_name: plan.name, label: cta.label, source: 'popup' });
  if (cta.downgrade) { onScheduleDowngrade(plan.name); setSelectedKey(null); }
  else if (cta.buy) onBuy(plan.name);
  else if (cta.action) cta.action();
}}
            className="w-full py-3.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#9333EA] to-[#EC4899] hover:shadow-lg disabled:opacity-50 transition"
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
})()}
      <EnterpriseModal open={showEnterprise} onClose={() => setShowEnterprise(false)} />
    </div>
  );
}